import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { clearSession, getAdmin } from "../auth";
import { label } from "../format";
import Icon from "./icons";

const MENU = [
  { title: "Overview", items: [{ to: "/admin", label: "Dashboard", icon: "dashboard", end: true }] },
  { title: "People", items: [{ to: "/admin/members", label: "Members", icon: "members" }] },
  {
    title: "Money",
    items: [
      { to: "/admin/packages", label: "Packages", icon: "packages" },
      { to: "/admin/deposits", label: "Deposits", icon: "deposits" },
      { to: "/admin/currencies", label: "BEP20 coins", icon: "coins" },
      { to: "/admin/bep20-deposits", label: "BEP20 deposits", icon: "chain" },
      { to: "/admin/bep20-sweep", label: "Sweep all users", icon: "sweep" },
      { to: "/admin/payout-wallet", label: "Payout wallet", icon: "wallet" },
      { to: "/admin/withdrawals", label: "Withdrawals", icon: "withdrawals" },
      { to: "/admin/purchases", label: "Purchases", icon: "purchases" },
      { to: "/admin/transfers", label: "Transfers", icon: "transfers" },
      { to: "/admin/earnings", label: "Earnings", icon: "earnings" },
      { to: "/admin/roi-failures", label: "ROI failures", icon: "roi" },
    ],
  },
  {
    title: "Rewards",
    items: [
      { to: "/admin/rewards", label: "Rewards", icon: "rewards" },
      { to: "/admin/reward-claims", label: "Claims", icon: "claims" },
    ],
  },
  {
    title: "Desk",
    items: [
      { to: "/admin/tickets", label: "Support", icon: "support" },
      { to: "/admin/content", label: "Content", icon: "content" },
      { to: "/admin/contacts", label: "Messages", icon: "messages" },
    ],
  },
  {
    title: "System",
    items: [
      { to: "/admin/settings", label: "Settings", icon: "settings" },
      { to: "/admin/staff", label: "Staff", icon: "staff", super: true },
    ],
  },
];

function currentPlace(pathname) {
  for (const group of MENU) {
    for (const item of group.items) {
      const match = item.end ? pathname === item.to || pathname === item.to + "/" : pathname.startsWith(item.to);
      if (match) return { group: group.title, label: item.label };
    }
  }
  return { group: "Overview", label: "Dashboard" };
}

function initial(name) {
  return String(name || "A").trim().charAt(0).toUpperCase() || "A";
}

export default function Layout() {
  const admin = getAdmin() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const place = currentPlace(location.pathname);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  async function onLogout() {
    setBusy(true);
    await api("/auth/logout", { method: "POST" }).catch(() => {});
    clearSession();
    navigate("/admin/login");
  }

  return (
    <div className="shell">
      {open ? <div className="backdrop" onClick={() => setOpen(false)} /> : null}
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="side-head">
          <NavLink to="/admin" className="brand brand-lockup" end>
            <img className="brand-logo" src="/finitygrowth-logo.svg" alt="Finitygrowth" />
            <em>Admin console</em>
          </NavLink>
          <button type="button" className="icon-btn side-close" onClick={() => setOpen(false)} aria-label="Close menu">
            <Icon name="close" />
          </button>
        </div>
        <nav className="side-scroll">
          {MENU.map((group) => {
            const items = group.items.filter((item) => !item.super || admin.role === "super_admin");
            if (!items.length) return null;
            return (
              <div key={group.title}>
                <div className="nav-label">{group.title}</div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">
          <div className="side-user">
            <span className="avatar letter">{initial(admin.name)}</span>
            <div>
              <strong>{admin.name || "Admin"}</strong>
              <span>{admin.email || label(admin.role)}</span>
            </div>
          </div>
          <button type="button" className="icon-btn" disabled={busy} onClick={onLogout} aria-label="Log out" title="Log out">
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button type="button" className="icon-btn menu-btn" onClick={() => setOpen(true)} aria-label="Open menu">
            <Icon name="menu" />
          </button>
          <div className="crumbs">
            <span>{place.group}</span>
            <strong>{place.label}</strong>
          </div>
          <div className="top-actions">
            <span className="date-chip">
              <Icon name="calendar" />
              {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </span>
            <div className="user-chip">
              <span className="avatar letter">{initial(admin.name)}</span>
              <div>
                <strong>{admin.name || "Admin"}</strong>
                <span>{label(admin.role)}</span>
              </div>
            </div>
          </div>
        </header>
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
